// components/TabComponents/FollowUpTab.jsx
import React, { useMemo, useState } from 'react';
import {
    Card,
    Typography,
    Button,
    Tag,
    Alert,
    Modal,
    Form,
    DatePicker,
    message,
    Timeline,
    Empty,
    Descriptions
} from 'antd';
import {
    CalendarOutlined,
    HistoryOutlined,
    CommentOutlined,
    UserOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import API from '../../../../API/API';
import { convertToPST } from '../../utils/dateUtils';

const { Title, Text } = Typography;
const PST_TIMEZONE = 'America/Los_Angeles';

export const formatPSTDate = (dateString, format = 'MM-DD-YYYY') => {
    if (!dateString) return 'N/A';
    return convertToPST(dayjs(dateString)).format(format);
};

const getActionTime = (item) => {
    const dateValue = item?.createdAt || item?.completedDate || item?.scheduledDate;
    const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getHandledByName = (handledBy) => {
    if (!handledBy || typeof handledBy !== 'object') return 'Unknown User';

    const firstLastName = `${handledBy.firstName || ''} ${handledBy.lastName || ''}`.trim();
    const name = handledBy.fullName
        || handledBy.name
        || firstLastName
        || handledBy.username
        || handledBy.userName
        || handledBy.email;

    if (!name) return 'Unknown User';
    return handledBy.role ? `${handledBy.role} - ${name}` : name;
};

const FollowUpTab = ({ lead, onUpdateSuccess }) => {
    const [loading, setLoading] = useState(false);

    const followUpHistory = useMemo(() => (
        [...(lead?.followUpHistory || [])]
            .map((item, index) => ({ ...item, originalIndex: index }))
            .sort((a, b) => (getActionTime(b) - getActionTime(a)) || (b.originalIndex - a.originalIndex))
    ), [lead?.followUpHistory]);

    const latestFollowUpNote = followUpHistory.find(item => item.notes && String(item.notes).trim())?.notes;

    const handleChangeFollowUpDate = () => {
        if (lead.leadStage !== 'Follow-Up') {
            message.error('This action is only allowed for leads in Follow-Up stage');
            return;
        }

        let newDate = null;
        Modal.confirm({
            title: 'Change Follow-up Date',
            content: (
                <div style={{ marginTop: 16 }}>
                    <Form layout="vertical">
                        <Form.Item label="New Follow-up Date & Time (PST)" required>
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Select new Follow-Up date and time (PST)"
                                defaultValue={lead.followUpDate ? convertToPST(dayjs(lead.followUpDate)) : null}
                                showTime={{ format: 'hh:mm A', use12Hours: true, defaultOpenValue: dayjs().tz(PST_TIMEZONE) }}
                                format="MMM DD, YYYY hh:mm A"
                                disabledDate={(current) => {
                                    const todayPST = dayjs().tz(PST_TIMEZONE).startOf('day');
                                    return current && current.startOf('day').isBefore(todayPST);
                                }}
                                onChange={(date) => {
                                    newDate = date || null;
                                }}
                            />
                        </Form.Item>
                    </Form>
                </div>
            ),
            onOk: async () => {
                if (!newDate) {
                    message.error('Please select a new Follow-Up date and time');
                    return false;
                }

                const pstDate = dayjs(newDate).tz(PST_TIMEZONE, true);
                if (pstDate.isBefore(dayjs().tz(PST_TIMEZONE))) {
                    message.error('Follow-up date/time cannot be in the past');
                    return false;
                }

                try {
                    setLoading(true);
                    const hideLoading = message.loading('Updating Follow-Up date...', 0);
                    const payload = {
                        followUpDate: pstDate.toISOString(),
                        comment: `Follow-up date changed from ${formatPSTDate(lead.followUpDate, 'MM-DD-YYYY hh:mm A')} PST to ${pstDate.format('MM-DD-YYYY hh:mm A')} PST`
                    };

                    const response = await API.UpdateLead(lead._id, payload);

                    hideLoading();
                    setLoading(false);

                    if (response?.status) {
                        onUpdateSuccess?.(response.data);
                        message.success('Follow-up date updated successfully!');
                    } else {
                        message.error(response?.message || response?.data?.message || 'Failed to update Follow-Up date');
                    }
                } catch (error) {
                    setLoading(false);
                    console.error('Error updating Follow-Up date:', error);
                    message.error('Failed to update Follow-Up date');
                }
            },
            okText: 'Update',
            cancelText: 'Cancel',
            width: 500
        });
    };

    const calculateFollowUpStatus = () => {
        if (!lead?.followUpDate) return null;

        const followUpDate = dayjs(lead.followUpDate);
        const now = dayjs();
        const totalHours = followUpDate.diff(now, 'hour', true);
        const totalMinutes = followUpDate.diff(now, 'minute');

        let boxBgColor = '#f0f9ff';
        let boxBorderColor = '#d0e7ff';
        let statusText = '';
        let tagColor = 'success';

        if (followUpDate.isBefore(now)) {
            boxBgColor = '#fff2f0';
            boxBorderColor = '#ffccc7';
            statusText = 'Missed';
            tagColor = 'error';
        } else if (totalHours <= 8) {
            boxBgColor = '#f6ffed';
            boxBorderColor = '#b7eb8f';
            tagColor = 'success';
        } else {
            boxBgColor = '#fff7e6';
            boxBorderColor = '#ffd591';
            tagColor = 'warning';
        }

        if (!followUpDate.isBefore(now)) {
            const days = Math.floor(totalMinutes / (60 * 24));
            const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
            const minutes = totalMinutes % 60;
            const parts = [];

            if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
            if (hours > 0) parts.push(`${hours}h`);
            if (minutes > 0) parts.push(`${minutes}m`);

            statusText = `In ${parts.join(' ')}`;
        }

        return {
            boxBgColor,
            boxBorderColor,
            statusText,
            tagColor
        };
    };

    const status = calculateFollowUpStatus();

    return (
        <Card>
            <div style={{ textAlign: 'center' }}>
                <CalendarOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                <Title level={4}>Follow Up Scheduled</Title>

                {lead?.followUpDate && status && (
                    <div
                        style={{
                            fontSize: 18,
                            marginBottom: 16,
                            padding: 12,
                            backgroundColor: status.boxBgColor,
                            borderRadius: 8,
                            border: `1px solid ${status.boxBorderColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <span>
                            <strong>{formatPSTDate(lead.followUpDate, 'MM-DD-YYYY hh:mm A')} PST</strong>
                        </span>

                        <Tag
                            color={status.tagColor}
                            style={{
                                fontSize: 14,
                                padding: '6px 12px',
                                fontWeight: 500,
                                margin: 0
                            }}
                        >
                            {status.statusText}
                        </Tag>
                    </div>
                )}

                {latestFollowUpNote && (
                    <Card
                        size="small"
                        style={{
                            marginBottom: 16,
                            textAlign: 'left',
                            backgroundColor: '#f9f9f9'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <CommentOutlined style={{ marginRight: 8, color: '#666', marginTop: 2 }} />
                            <div>
                                <Text strong style={{ fontSize: 14 }}>Latest Follow-up Note:</Text>
                                <div style={{ marginTop: 4, color: '#555' }}>
                                    {latestFollowUpNote}
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                <Button
                    type="primary"
                    icon={<CalendarOutlined />}
                    onClick={handleChangeFollowUpDate}
                    loading={loading}
                    disabled={lead.leadStage !== 'Follow-Up'}
                    style={{ marginTop: 16, marginBottom: 24 }}
                >
                    Change Follow-up Date
                </Button>

                {lead.leadStage !== 'Follow-Up' && (
                    <Alert
                        message="Note"
                        description="This lead is not currently in Follow-Up stage. Change the lead stage to Follow-Up to schedule Follow-Up dates."
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                )}

                <div style={{ textAlign: 'left', marginTop: 16 }}>
                    <Title level={5}>
                        <HistoryOutlined /> Follow-Up History
                    </Title>

                    {followUpHistory.length === 0 ? (
                        <Empty description="No follow-up history yet" />
                    ) : (
                        <Timeline mode="left" style={{ marginLeft: 20 }}>
                            {followUpHistory.map((item, index) => (
                                <Timeline.Item
                                    key={item._id || `${item.scheduledDate}-${index}`}
                                    color={index === 0 ? 'blue' : 'green'}
                                    dot={index === 0 ? <HistoryOutlined /> : <ClockCircleOutlined />}
                                >
                                    <Card size="small" style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                                            <Text strong>Follow-Up #{followUpHistory.length - index}</Text>
                                            <Tag color={item.status === 'Completed' ? 'green' : 'blue'} style={{ margin: 0 }}>
                                                {item.status || 'Scheduled'}
                                            </Tag>
                                        </div>

                                        <Descriptions column={1} size="small" colon>
                                            <Descriptions.Item label="Follow-Up Date">
                                                {formatPSTDate(item.scheduledDate, 'MM-DD-YYYY')}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Follow-Up Time">
                                                {formatPSTDate(item.scheduledDate, 'hh:mm A')} PST
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Changed By / Scheduled By">
                                                <UserOutlined style={{ marginRight: 6 }} />
                                                {getHandledByName(item.handledBy)}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Comment / Note">
                                                {item.notes || 'No note'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created/Changed At">
                                                {formatPSTDate(item.createdAt || item.completedDate || item.scheduledDate, 'MM-DD-YYYY hh:mm A')} PST
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default FollowUpTab;
